import { supabase } from '../utils/supabase';

class ApiEndpoints {
  // Productos
  static async getProducts() {
    console.log('🔥 ApiEndpoints.getProducts() called');
    try {
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          categories (
            id,
            name,
            color,
            icon
          )
        `)
        .eq('published', true)
        .order('name', { ascending: true });

      if (error) {
        console.error('❌ Error fetching products:', error);
        throw error;
      }

      console.log('✅ Products fetched:', data?.length || 0, 'items');
      return data || [];
    } catch (error) {
      console.error('❌ Error in getProducts:', error);
      return [];
    }
  }

  static async getProductById(id) {
    console.log('🔥 ApiEndpoints.getProductById() called with id:', id);
    try {
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          categories (
            id,
            name,
            color,
            icon
          )
        `)
        .eq('id', parseInt(id))
        .single();

      if (error) {
        console.error('❌ Error fetching product:', error);
        throw error;
      }

      console.log('✅ Product fetched:', data);
      return data;
    } catch (error) {
      console.error('❌ Error in getProductById:', error);
      return null;
    }
  }

  static async uploadImage(file) {
    console.log('🔥 ApiEndpoints.uploadImage() called');
    try {
      const fileName = `${Date.now()}_${file.name}`;
      const filePath = `public/${fileName}`;
      
      const { data, error } = await supabase.storage
        .from('product-images')
        .upload(filePath, file);

      if (error) {
        console.error('❌ Error uploading image:', error);
        throw error;
      }

      const { data: publicUrl } = await supabase.storage
        .from('product-images')
        .getPublicUrl(filePath);

      console.log('✅ Image uploaded:', publicUrl.publicUrl);
      return publicUrl.publicUrl;
    } catch (error) {
      console.error('❌ Error in uploadImage:', error);
      return null;
    }
  }

  static async uploadMultipleImages(files) {
    console.log('🔥 ApiEndpoints.uploadMultipleImages() called');
    try {
      const uploadPromises = files.map(file => this.uploadImage(file));
      const urls = await Promise.all(uploadPromises);
      
      // Filtrar URLs nulas (errores de subida)
      const validUrls = urls.filter(url => url !== null);
      return validUrls;
    } catch (error) {
      console.error('❌ Error in uploadMultipleImages:', error);
      return [];
    }
  }

  static async deleteImage(url) {
    console.log('🔥 ApiEndpoints.deleteImage() called');
    try {
      // Extraer el path de la URL
      const urlParts = url.split('/');
      const filePath = urlParts[urlParts.length - 1];
      
      const { error } = await supabase.storage
        .from('product-images')
        .remove([filePath]);

      if (error) {
        console.error('❌ Error deleting image:', error);
        throw error;
      }

      console.log('✅ Image deleted:', url);
      return true;
    } catch (error) {
      console.error('❌ Error in deleteImage:', error);
      return false;
    }
  }

  static async createProduct(productData) {
    console.log('🔥 ApiEndpoints.createProduct() called');
    try {
      // Asegurarse de que images sea un array
      const images = Array.isArray(productData.images) ? productData.images : [];
      
      const { data, error } = await supabase
        .from('products')
        .insert([{
          ...productData,
          images: images,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) {
        console.error('❌ Error creating product:', error);
        throw error;
      }

      console.log('✅ Product created:', data);
      return data;
    } catch (error) {
      console.error('❌ Error in createProduct:', error);
      return null;
    }
  }

  static async updateProduct(id, updateData) {
    console.log('🔥 ApiEndpoints.updateProduct() called for id:', id);
    try {
      // Si hay imágenes nuevas en updateData, procesarlas
      let finalUpdateData = { ...updateData };
      if (updateData.images && Array.isArray(updateData.images)) {
        finalUpdateData.images = updateData.images;
      }

      const { data, error } = await supabase
        .from('products')
        .update({
          ...finalUpdateData,
          updated_at: new Date().toISOString()
        })
        .eq('id', parseInt(id))
        .select()
        .single();

      if (error) {
        console.error('❌ Error updating product:', error);
        throw error;
      }

      console.log('✅ Product updated:', data);
      return data;
    } catch (error) {
      console.error('❌ Error in updateProduct:', error);
      return null;
    }
  }

  static async deleteProduct(id) {
    console.log('🔥 ApiEndpoints.deleteProduct() called for id:', id);
    try {
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', parseInt(id));

      if (error) {
        console.error('❌ Error deleting product:', error);
        throw error;
      }

      console.log('✅ Product deleted successfully');
      return true;
    } catch (error) {
      console.error('❌ Error in deleteProduct:', error);
      return false;
    }
  }

  // Categorías
  static async getCategories() {
    console.log('🔥 ApiEndpoints.getCategories() called');
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('active', true)
        .order('name', { ascending: true });

      if (error) {
        console.error('❌ Error fetching categories:', error);
        throw error;
      }

      console.log('✅ Categories fetched:', data?.length || 0, 'items');
      return data || [];
    } catch (error) {
      console.error('❌ Error in getCategories:', error);
      return [];
    }
  }

  static async getCategoryById(id) {
    console.log('🔥 ApiEndpoints.getCategoryById() called with id:', id);
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('id', parseInt(id))
        .single();

      if (error) {
        console.error('❌ Error fetching category:', error);
        throw error;
      }

      console.log('✅ Category fetched:', data);
      return data;
    } catch (error) {
      console.error('❌ Error in getCategoryById:', error);
      return null;
    }
  }

  static async createCategory(categoryData) {
    console.log('🔥 ApiEndpoints.createCategory() called');
    try {
      const { data, error } = await supabase
        .from('categories')
        .insert([{
          ...categoryData,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) {
        console.error('❌ Error creating category:', error);
        throw error;
      }

      console.log('✅ Category created:', data);
      return data;
    } catch (error) {
      console.error('❌ Error in createCategory:', error);
      return null;
    }
  }

  static async updateCategory(id, updateData) {
    console.log('🔥 ApiEndpoints.updateCategory() called for id:', id);
    try {
      const { data, error } = await supabase
        .from('categories')
        .update({
          ...updateData,
          updated_at: new Date().toISOString()
        })
        .eq('id', parseInt(id))
        .select()
        .single();

      if (error) {
        console.error('❌ Error updating category:', error);
        throw error;
      }

      console.log('✅ Category updated:', data);
      return data;
    } catch (error) {
      console.error('❌ Error in updateCategory:', error);
      return null;
    }
  }

  static async deleteCategory(id) {
    console.log('🔥 ApiEndpoints.deleteCategory() called for id:', id);
    try {
      const { error } = await supabase
        .from('categories')
        .delete()
        .eq('id', parseInt(id));

      if (error) {
        console.error('❌ Error deleting category:', error);
        throw error;
      }

      console.log('✅ Category deleted successfully');
      return true;
    } catch (error) {
      console.error('❌ Error in deleteCategory:', error);
      return false;
    }
  }

  // Pedidos
  static async getOrders() {
    console.log('🔥 ApiEndpoints.getOrders() called');
    try {
      const { data, error } = await supabase
        .from('orders')
        .select('*, order_items(id, product_id, name, price, discount_percentage, discounted_price, quantity, subtotal), delivery_info(id, assigned_to, estimated_date, estimated_time, tracking_number, notes), order_status_history(id, status, date, note)')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Error fetching orders:', error);
        throw error;
      }

      console.log('✅ Orders fetched:', data?.length || 0, 'items');
      return data || [];
    } catch (error) {
      console.error('❌ Error in getOrders:', error);
      return [];
    }
  }

  static async getOrderById(id) {
    console.log('🔥 ApiEndpoints.getOrderById() called with id:', id);
    try {
      const { data, error } = await supabase
        .from('orders')
        .select('*, order_items(id, product_id, name, price, discount_percentage, discounted_price, quantity, subtotal), delivery_info(id, assigned_to, estimated_date, estimated_time, tracking_number, notes), order_status_history(id, status, date, note)')
        .eq('id', parseInt(id))
        .single();

      if (error) {
        console.error('❌ Error fetching order:', error);
        throw error;
      }

      console.log('✅ Order fetched:', data);
      return data;
    } catch (error) {
      console.error('❌ Error in getOrderById:', error);
      return null;
    }
  }

  static async createOrder(orderData) {
    console.log('🔥 ApiEndpoints.createOrder() called');
    try {
      // Crear el pedido principal
      const { data: order, error } = await supabase
        .from('orders')
        .insert([{
          customer_name: orderData.customerName,
          customer_phone: orderData.customerPhone,
          customer_address: orderData.customerAddress,
          status: 'pending',
          total: orderData.total,
          observations: orderData.observations || '',
          terms_accepted: orderData.termsAccepted,
          privacy_accepted: orderData.privacyAccepted
        }])
        .select()
        .single();

      if (error) {
        console.error('❌ Error creating order:', error);
        throw error;
      }

      const orderId = order?.id;
      console.log('✅ Order created with ID:', orderId);

      // Crear los items del pedido
      if (orderId && orderData.items?.length > 0) {
        const orderItems = orderData.items.map(item => ({
          order_id: orderId,
          product_id: parseInt(item.id),
          name: item.name,
          price: item.price,
          discount_percentage: item.discountPercentage || null,
          discounted_price: item.discountedPrice || null,
          quantity: item.quantity,
          subtotal: item.subtotal
        }));

        const { error: itemsError } = await supabase
          .from('order_items')
          .insert(orderItems);

        if (itemsError) {
          console.error('❌ Error creating order items:', itemsError);
          throw itemsError;
        }

        console.log('✅ Order items created:', orderItems.length, 'items');
      }

      // Crear entrada inicial en el historial
      await supabase
        .from('order_status_history')
        .insert([{
          order_id: orderId,
          status: 'pending',
          note: 'Pedido creado'
        }]);

      return order;
    } catch (error) {
      console.error('❌ Error in createOrder:', error);
      return null;
    }
  }

  static async updateOrderStatus(orderId, status, note = '') {
    console.log('🔥 ApiEndpoints.updateOrderStatus() called for order:', orderId);
    try {
      // Agregar al historial
      await supabase
        .from('order_status_history')
        .insert([{
          order_id: parseInt(orderId),
          status: status,
          note: note,
          date: new Date().toISOString()
        }]);

      // Actualizar el estado del pedido
      const { data, error } = await supabase
        .from('orders')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', parseInt(orderId))
        .select()
        .single();

      if (error) {
        console.error('❌ Error updating order status:', error);
        throw error;
      }

      console.log('✅ Order status updated:', status);
      return data;
    } catch (error) {
      console.error('❌ Error in updateOrderStatus:', error);
      return null;
    }
  }

  // Stats
  static async getStats() {
    console.log('🔥 ApiEndpoints.getStats() called');
    try {
      // Obtener conteo de productos
      const { count: totalProducts, error: productsError } = await supabase
        .from('products')
        .select('*', { count: 'exact', head: true });

      // Obtener conteo de pedidos
      const { count: totalOrders, error: ordersError } = await supabase
        .from('orders')
        .select('*', { count: 'exact', head: true });

      // Obtener conteo de categorías activas
      const { count: activeCategories, error: categoriesError } = await supabase
        .from('categories')
        .select('*', { count: 'exact', head: true });

      // Obtener sumatoria de totales de pedidos
      const { data: revenueData, error: revenueError } = await supabase
        .from('orders')
        .select('total');

      if (productsError || ordersError || categoriesError || revenueError) {
        throw new Error('Error fetching stats');
      }

      const totalRevenue = revenueData?.reduce((sum, order) => sum + order.total, 0) || 0;
      const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

      const stats = {
        totalProducts: totalProducts || 0,
        totalOrders: totalOrders || 0,
        activeCategories: activeCategories || 0,
        totalRevenue,
        averageOrderValue
      };

      console.log('✅ Stats calculated:', stats);
      return stats;
    } catch (error) {
      console.error('❌ Error in getStats:', error);
      return {
        totalProducts: 0,
        totalOrders: 0,
        activeCategories: 0,
        totalRevenue: 0,
        averageOrderValue: 0
      };
    }
  }
}

export default ApiEndpoints;